/**
 * EVAR (Endovascular Aortic Repair) Workflow Service.
 *
 * Provides domain-specific access to loaded visualization data for EVAR cases:
 * - CT volume for 2D rendering (orthogonal slices, MPR, centerline views)
 * - Vascular segmentation for 3D rendering (mask on CT)
 * - Vascular description (centerlines, keypoints) for measurements
 */

import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';

import { StrategyStateService } from './strategy-state-service';
import { EVAR_TEMPLATE } from '../domain/evar-template';

import type { ServiceContext } from '@/visualization/core/types';
import { DisplayFeature } from '@/visualization/display/display-feature';
import { DisplayFeatureType } from '@/visualization/display/types';
import type { SizingDisplayData, SizingDraftDisplay } from '@/visualization/display/types';
import { CarmView, CarmViewFactory } from '@/visualization/domain/carm-view';
import { Centerline, CenterlineFactory } from '@/visualization/domain/centerline';
import { Keypoint, KeypointFactory } from '@/visualization/domain/keypoint';
import { LabelMap } from '@/visualization/domain/label-map';
import {
    resolveAnglePreviewWedge,
    type AngleSlot,
} from '@/visualization/domain/sizing/computation/angle-calculator';
import { collectPathPoints } from '@/visualization/domain/sizing/computation/path-resolver';
import type { ILabelMapStructure, IVascularDescriptionData, Vec3 } from '@/visualization/domain/types';
import { Volume } from '@/visualization/domain/volume';
import { CurrentLocation, CurrentLocationService } from '@/visualization/services';
import { CarmViewService } from '@/visualization/services/carm-view-service';
import { SizingStateService } from '@/visualization/services/sizing/sizing-state-service';
import { ViewportDataService } from '@/visualization/services/viewport-data-service';
import {
    applyMaskToVolume,
    convertItkToVtkImage,
    convertVtkToItkImage,
    resampleVolumeToSpacing,
} from '@/visualization/utils';
import { BaseWorkflowService } from '@/visualization/workflows/base-workflow-service';

// ============================================================================
// Types
// ============================================================================

/**
 * EVAR slot configuration.
 */
const EVAR_SLOTS = {
    /** CT Angiography volume - for 2D rendering */
    ctVolume: 'ct-angio',
    /** Vascular segmentation - mask for 3D rendering */
    segmentation: 'seg-vascular',
    /** Vascular description JSON - centerlines, keypoints */
    vascularDescription: 'vascular-description',
} as const;

/**
 * Event payloads for EvarWorkflowService.
 */
export interface DataReadyPayload {
    ctVolume: boolean;
    segmentation: boolean;
    vascularDescription: boolean;
}

// ============================================================================
// EvarWorkflowService
// ============================================================================

export class EvarWorkflowService extends BaseWorkflowService {
    static readonly SERVICE_NAME = 'EvarWorkflowService';
    readonly WORKFLOW_ID = 'evar';

    override readonly EVENTS = {
        LOAD_PROGRESS: 'workflow:load:progress',
        LOAD_COMPLETE: 'workflow:load:complete',
        TRANSFORM_ERROR: 'workflow:transform:error',
        CASE_METADATA_SET: 'workflow:case-metadata:set',
        DATA_READY: 'evar:data:ready',
    } as const;

    // Domain objects
    private workflowUnsubscribers: Array<() => void> = [];
    private ctVolume: Volume | null = null;
    private segmentation: LabelMap | null = null;
    private maskedCTVolume: Volume | null = null;
    private centerlines: Map<string, Centerline> = new Map();
    private keypoints: Map<string, Keypoint> = new Map();
    private carmViews: CarmView[] = [];

    constructor(context: ServiceContext) {
        super(context);
    }

    // ========================================================================
    // Template Method Implementations
    // ========================================================================

    protected getDataSlots(): string[] {
        return [EVAR_SLOTS.ctVolume, EVAR_SLOTS.segmentation, EVAR_SLOTS.vascularDescription];
    }

    protected transformLoadedData(): void {
        this.transformCTVolume();
        this.transformSegmentation();
        this.transformVascularDescription();

        this.createMaskedCTVolume();

        this.populateDataSlots();

        this.initializeCurrentLocation();
        this.initializeCarmViews();
        this.initializeSizing();
        this.initializeStrategy();

        this.broadcast(this.EVENTS.DATA_READY, {
            ctVolume: this.ctVolume !== null,
            segmentation: this.segmentation !== null,
            vascularDescription: this.centerlines.size > 0 || this.keypoints.size > 0,
        } satisfies DataReadyPayload);
    }

    protected createDisplayFeatures(): void {
        const locationService = this.context.services.getService<CurrentLocationService>(
            CurrentLocationService.SERVICE_NAME
        );

        const currentLocation = locationService.getLocation();

        if (this.hasVascularDescription()) {
            this.displayManager.addFeature(
                new DisplayFeature({
                    id: `${this.WORKFLOW_ID}-centerlines`,
                    type: DisplayFeatureType.CENTERLINE,
                    data: this.centerlines,
                    properties: {
                        tubeDiameter: 0.4,
                        activeTubeDiameter: 0.8,
                        activeCenterlineId: currentLocation?.centerlinePosition?.centerlineId,
                    },
                })
            );
        }

        this.displayManager.addFeature(
            new DisplayFeature({
                id: `${this.WORKFLOW_ID}-current-location`,
                type: DisplayFeatureType.CURRENT_LOCATION,
                data: currentLocation,
                properties: {
                    opacity: 1,
                    sphere: { radius: 2.5, color: [1, 0, 0] },
                    crosshair: { color: '#00FF00' },
                },
            })
        );

        this.createSizingFeature();
        this.subscribeToCurrentLocationChanges();
    }

    private createSizingFeature(): void {
        const sizingService = this.context.services.getService<SizingStateService>(
            SizingStateService.SERVICE_NAME
        );
        const locationService = this.context.services.getService<CurrentLocationService>(
            CurrentLocationService.SERVICE_NAME
        );

        if (!sizingService.getTemplate()) return;

        const featureId = `${this.WORKFLOW_ID}-sizing`;

        const getCurrentNodeId = (): number | null => {
            const loc = locationService.getLocation();
            if (!loc?.centerlinePosition) return null;
            const cl = this.centerlines.get(loc.centerlinePosition.centerlineId);
            if (!cl) return null;
            return cl.getNodeAt(loc.centerlinePosition.nodeIndex).id;
        };

        const resolveDraft = (): SizingDraftDisplay | null => {
            const draft = sizingService.getDraft();
            if (!draft) return null;
            const slots =
                draft.kind === 'length' ? (['start', 'end'] as const) : (['p1', 'vertex', 'p2'] as const);
            const nodePool = sizingService.getNodePool();
            const points: Vec3[] = [];
            for (const slot of slots) {
                const nodeId = draft.points[slot];
                if (nodeId == null) continue;
                const node = nodePool.get(nodeId);
                if (node) points.push(node.point);
            }

            // Live preview: the current location fills the next unplaced slot until
            // the user validates it. Length traces a white tube along the centerline
            // from the placed start; angle traces a white arm/overlay to the cursor.
            let previewPoint: Vec3 | null = null;
            let previewPath: Vec3[] | null = null;
            let previewLengthNodeIds: [number, number] | null = null;
            let previewAngle: SizingDraftDisplay['previewAngle'] = null;

            const nextSlot = slots.find((slot) => draft.points[slot] == null);
            const currentNodeId = getCurrentNodeId();
            if (nextSlot && currentNodeId != null) {
                previewPoint = nodePool.get(currentNodeId)?.point ?? null;

                if (draft.kind === 'length' && draft.points.start != null && previewPoint) {
                    const path = collectPathPoints(
                        draft.points.start,
                        currentNodeId,
                        sizingService.getCenterlines()
                    );
                    if (path.length >= 2) {
                        previewPath = path;
                        previewLengthNodeIds = [draft.points.start, currentNodeId];
                    }
                }

                if (draft.kind === 'angle' && previewPoint) {
                    // Resolve the wedge by slot so the vertex tracks its real slot
                    // regardless of the order P1/Vertex/P2 were placed in.
                    const slotPos = (slot: AngleSlot): Vec3 | null => {
                        const nodeId = draft.points[slot];
                        if (nodeId == null) return null;
                        return nodePool.get(nodeId)?.point ?? null;
                    };
                    previewAngle = resolveAnglePreviewWedge(
                        { p1: slotPos('p1'), vertex: slotPos('vertex'), p2: slotPos('p2') },
                        nextSlot as AngleSlot,
                        previewPoint
                    );
                }
            }

            return {
                kind: draft.kind,
                points,
                previewPoint,
                previewPath,
                previewLengthNodeIds,
                previewAngle,
            };
        };

        const buildSizingData = (): SizingDisplayData => ({
            placements: sizingService.getAllPlacements(),
            ghostPlacements: sizingService.getGhostPlacements(),
            measurements: sizingService.getAllMeasurements(),
            nodePool: sizingService.getNodePool(),
            diameterEdits: sizingService.getAllDiameterEdits(),
            centerlines: sizingService.getCenterlines(),
            selectedMeasurementId: sizingService.getSelectedMeasurementId(),
            currentNodeId: getCurrentNodeId(),
            activeCenterlineId: locationService.getActiveCenterline()?.id ?? null,
            draft: resolveDraft(),
        });

        this.displayManager.addFeature(
            new DisplayFeature({
                id: featureId,
                type: DisplayFeatureType.SIZING,
                data: buildSizingData(),
                properties: {
                    keypointColor: '#00ccff',
                    diameterColor: '#ff8844',
                    lengthColor: '#88ff44',
                    fontSize: 11,
                },
            })
        );

        const updateSizingData = () => {
            this.displayManager.updateData<DisplayFeatureType.SIZING>(featureId, buildSizingData());
        };

        this.workflowUnsubscribers.push(
            sizingService.subscribe(sizingService.EVENTS.MEASUREMENTS_UPDATED, updateSizingData),
            sizingService.subscribe(sizingService.EVENTS.KEYPOINT_MOVED, updateSizingData),
            sizingService.subscribe(sizingService.EVENTS.KEYPOINT_RESET, updateSizingData),
            sizingService.subscribe(sizingService.EVENTS.DIAMETER_EDITED, updateSizingData),
            sizingService.subscribe(sizingService.EVENTS.DIAMETER_RESTORED, updateSizingData),
            sizingService.subscribe(sizingService.EVENTS.MEASUREMENT_RELOCATED, updateSizingData),
            sizingService.subscribe(sizingService.EVENTS.CUSTOM_MEASUREMENT_ADDED, updateSizingData),
            sizingService.subscribe(sizingService.EVENTS.CUSTOM_MEASUREMENT_REMOVED, updateSizingData),
            sizingService.subscribe(sizingService.EVENTS.CUSTOM_MEASUREMENT_DRAFT_CHANGED, updateSizingData),
            sizingService.subscribe(sizingService.EVENTS.OFFSET_ADDED, updateSizingData),
            sizingService.subscribe(sizingService.EVENTS.OFFSET_REMOVED, updateSizingData),
            sizingService.subscribe(sizingService.EVENTS.MEASUREMENT_SELECTION_CHANGED, updateSizingData),
            sizingService.subscribe(sizingService.EVENTS.SIZING_INITIALIZED, updateSizingData),
            locationService.subscribe(locationService.EVENTS.LOCATION_CHANGED, updateSizingData)
        );
    }

    private subscribeToCurrentLocationChanges(): void {
        const locationService = this.context.services.getService<CurrentLocationService>(
            CurrentLocationService.SERVICE_NAME
        );

        this.workflowUnsubscribers.push(
            locationService.subscribe<Centerline | null>(
                locationService.EVENTS.ACTIVE_CENTERLINE_CHANGED,
                (centerline) => {
                    this.displayManager.updateFeature(`${this.WORKFLOW_ID}-centerlines`, {
                        activeCenterlineId: centerline?.id ?? undefined,
                    });
                }
            )
        );
        this.workflowUnsubscribers.push(
            locationService.subscribe<CurrentLocation>(
                locationService.EVENTS.LOCATION_CHANGED,
                (currentLocation) => {
                    this.displayManager.updateData<DisplayFeatureType.CURRENT_LOCATION>(
                        `${this.WORKFLOW_ID}-current-location`,
                        currentLocation
                    );
                }
            )
        );
    }

    // ========================================================================
    // Domain Transformation Methods
    // ========================================================================

    private transformCTVolume(): void {
        const imageData = this.dataLoader.getVolumeData(EVAR_SLOTS.ctVolume);
        if (!imageData) {
            console.warn(
                `[EvarWorkflow] Expected volume data for slot "${EVAR_SLOTS.ctVolume}" but got null`
            );
            return;
        }
        this.ctVolume = new Volume(EVAR_SLOTS.ctVolume, imageData);
    }

    private transformSegmentation(): void {
        const imageData = this.dataLoader.getVolumeData(EVAR_SLOTS.segmentation);
        if (!imageData) {
            console.warn(
                `[EvarWorkflow] Expected volume data for slot "${EVAR_SLOTS.segmentation}" but got null`
            );
            return;
        }

        const metadata = this.dataLoader.getSlotMetadata(EVAR_SLOTS.segmentation);
        const structures = this.extractStructures(metadata);
        this.segmentation = new LabelMap(EVAR_SLOTS.segmentation, imageData, structures);
    }

    private transformVascularDescription(): void {
        const jsonData = this.dataLoader.getJsonData<IVascularDescriptionData>(
            EVAR_SLOTS.vascularDescription
        );

        if (!jsonData) {
            console.warn(
                `[EvarWorkflow] Expected JSON data for slot "${EVAR_SLOTS.vascularDescription}" but got null`
            );
            return;
        }

        if (jsonData.centerlines) {
            this.centerlines = CenterlineFactory.createFromVascularDescription(jsonData.centerlines);
        }

        if (jsonData.keypoints) {
            this.keypoints = KeypointFactory.createFromVascularDescription(jsonData.keypoints);
        }

        if (jsonData.carmAngles && jsonData.carmAngles.length > 0) {
            this.carmViews = CarmViewFactory.createFromVascularDescription(jsonData.carmAngles);
        }
    }

    private createMaskedCTVolume(): void {
        if (!this.ctVolume || !this.segmentation) {
            return;
        }

        const resampledCTVolume = resampleVolumeToSpacing(this.ctVolume.imageData, [1.5, 1.5, 1.5]);
        const itkVolume = convertVtkToItkImage(resampledCTVolume);
        const itkMask = convertVtkToItkImage(this.segmentation.imageData);
        const itkMaskedVolume = applyMaskToVolume(itkVolume, itkMask);

        if (!itkMaskedVolume) {
            const message = 'Failed to create masked CT volume for 3D rendering';
            console.error(`[EvarWorkflow] ${message}`);
            this.broadcast(this.EVENTS.TRANSFORM_ERROR, { message });
            return;
        }

        const vtkMaskedVolume = convertItkToVtkImage(itkMaskedVolume);

        this.maskedCTVolume = new Volume('masked-ct-volume', vtkMaskedVolume);
    }

    private populateDataSlots(): void {
        const dataService = this.context.services.getService<ViewportDataService>(
            ViewportDataService.SERVICE_NAME
        );

        if (this.ctVolume) {
            dataService.setSlot('ct-volume', this.ctVolume);
        }

        if (this.maskedCTVolume) {
            dataService.setSlot('rendering-volume', this.maskedCTVolume);
        }

        if (this.centerlines.size > 0) {
            dataService.setSlot('centerlines', this.centerlines);
        }
    }

    initializeCurrentLocation() {
        if (this.centerlines.size === 0) return;

        const [, centerline] = this.centerlines.entries().next().value!;
        const midIndex = Math.round(centerline.nodeCount / 2);

        const locationService = this.context.services.getService<CurrentLocationService>(
            CurrentLocationService.SERVICE_NAME
        );

        locationService.setCurrentCenterlinePosition(centerline.id, midIndex);
    }

    private initializeSizing(): void {
        if (!this.hasVascularDescription()) return;

        const sizingService = this.context.services.getService<SizingStateService>(
            SizingStateService.SERVICE_NAME
        );

        sizingService.initialize(EVAR_TEMPLATE, this.centerlines, this.keypoints);
    }

    private initializeStrategy(): void {
        const strategyService = this.context.services.getService<StrategyStateService>(
            StrategyStateService.SERVICE_NAME
        );
        strategyService.initialize();

        // When measurements change, mark strategy assembly entries as stale
        const sizingService = this.context.services.getService<SizingStateService>(
            SizingStateService.SERVICE_NAME
        );
        this.workflowUnsubscribers.push(
            sizingService.subscribe(sizingService.EVENTS.MEASUREMENTS_UPDATED, () => {
                strategyService.markStale();
            })
        );
    }

    private initializeCarmViews(): void {
        if (this.carmViews.length === 0) return;

        const carmService = this.context.services.getService<CarmViewService>(CarmViewService.SERVICE_NAME);
        carmService.initialize(this.carmViews);
    }

    private extractStructures(metadata: Record<string, unknown> | undefined): ILabelMapStructure[] {
        if (metadata && Array.isArray(metadata.structures)) {
            return metadata.structures as ILabelMapStructure[];
        }
        console.warn('No structures found in segmentation metadata');
        return [];
    }

    // ========================================================================
    // Domain Object Accessors
    // ========================================================================

    getCTVolume(): Volume | undefined {
        return this.ctVolume ?? undefined;
    }

    getCTImageData(): vtkImageData | undefined {
        return this.ctVolume?.imageData;
    }

    getSegmentation(): LabelMap | undefined {
        return this.segmentation ?? undefined;
    }

    getSegmentationImageData(): vtkImageData | undefined {
        return this.segmentation?.imageData;
    }

    getMaskedCTVolume(): Volume | undefined {
        return this.maskedCTVolume ?? undefined;
    }

    getMaskedCTImageData(): vtkImageData | undefined {
        return this.maskedCTVolume?.imageData;
    }

    getCenterlines(): Map<string, Centerline> {
        return this.centerlines;
    }

    getCenterline(name: string): Centerline | undefined {
        return this.centerlines.get(name);
    }

    getKeypoints(): Map<string, Keypoint> {
        return this.keypoints;
    }

    getKeypoint(name: string): Keypoint | undefined {
        return this.keypoints.get(name);
    }

    isReady(): boolean {
        return this.ctVolume !== null;
    }

    hasVascularDescription(): boolean {
        return this.centerlines.size > 0 || this.keypoints.size > 0;
    }

    override reset(): void {
        console.log(`${EvarWorkflowService.SERVICE_NAME} reset called`);

        this.workflowUnsubscribers.forEach((unsub) => unsub());
        this.workflowUnsubscribers = [];

        const dataService = this.context.services.getService<ViewportDataService>(
            ViewportDataService.SERVICE_NAME
        );
        dataService.clearAll();

        this.ctVolume = null;
        this.segmentation = null;
        this.maskedCTVolume = null;
        this.centerlines.clear();
        this.keypoints.clear();
        this.carmViews = [];

        const carmService = this.context.services.getService<CarmViewService>(CarmViewService.SERVICE_NAME);
        carmService.reset();

        const strategyService = this.context.services.getService<StrategyStateService>(
            StrategyStateService.SERVICE_NAME
        );
        strategyService.reset();

        super.reset();
    }
}
